import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card,
  DatePicker,
  Table,
  Typography,
  Space,
  Row,
  Col,
  Statistic,
  Button,
  Spin,
  message,
  Empty
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import { ReloadOutlined } from '@ant-design/icons';
import { api } from '../services/api';
import { useCurrency } from '../contexts/CurrencyContext';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

interface ProfitabilityRow {
  productId: number;
  productName: string;
  quantity: number;
  salesAmount: number;
  costAmount: number;
  profit: number;
  margin: number;
}

interface ProfitabilityResponse {
  rows: ProfitabilityRow[];
  totals: {
    salesAmount: number;
    costAmount: number;
    profit: number;
    margin: number;
  };
  startDate?: string;
  endDate?: string;
}

const DEFAULT_RANGE_DAYS = 10;

const ProfitabilityReport: React.FC = () => {
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(DEFAULT_RANGE_DAYS, 'day').startOf('day'),
    dayjs().endOf('day')
  ]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ProfitabilityResponse | null>(null);

  const { baseCurrencySymbol } = useCurrency();

  const formatCurrency = useMemo(
    () =>
      (value: number) =>
        new Intl.NumberFormat('tr-TR', {
          style: 'currency',
          currency: 'TRY',
          currencyDisplay: 'symbol',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(value ?? 0).replace('TRY', baseCurrencySymbol || '₺'),
    [baseCurrencySymbol]
  );

  const formatMargin = (value: number) =>
    `${value?.toFixed(2) ?? '0.00'} %`;

  const loadData = useCallback(
    async (range: [Dayjs, Dayjs]) => {
      if (!range || !range[0] || !range[1]) {
        return;
      }

      try {
        setLoading(true);

        const params = {
          startDate: range[0].startOf('day').toISOString(),
          endDate: range[1].endOf('day').toISOString()
        };

        const { data } = await api.get<ProfitabilityResponse>('/stock/profitability', { params });
        setData(data);
      } catch (error: any) {
        console.error('Profitability report load error:', error);
        message.error(error?.response?.data?.message || 'Kârlılık raporu yüklenemedi');
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    loadData(dateRange);
  }, [dateRange, loadData]);

  const handleRangeChange = (values: null | [Dayjs | null, Dayjs | null]) => {
    if (!values || !values[0] || !values[1]) {
      setDateRange([dayjs().subtract(DEFAULT_RANGE_DAYS, 'day').startOf('day'), dayjs().endOf('day')]);
      return;
    }
    setDateRange([values[0], values[1]]);
  };

  const columns: ColumnsType<ProfitabilityRow> = [
    {
      title: 'Ürün',
      dataIndex: 'productName',
      key: 'productName',
      render: (name: string, record: ProfitabilityRow) =>
        name?.trim() || (record.productId ? `Ürün #${record.productId}` : '-')
    },
    {
      title: 'Satış Tutarı',
      dataIndex: 'salesAmount',
      key: 'salesAmount',
      align: 'right',
      render: (value: number) => <Text>{formatCurrency(value)}</Text>
    },
    {
      title: 'Maliyet',
      dataIndex: 'costAmount',
      key: 'costAmount',
      align: 'right',
      render: (value: number) => <Text>{formatCurrency(value)}</Text>
    },
    {
      title: 'Kâr',
      dataIndex: 'profit',
      key: 'profit',
      align: 'right',
      render: (value: number) => (
        <Text type={value >= 0 ? 'success' : 'danger'}>{formatCurrency(value)}</Text>
      )
    },
    {
      title: 'Kâr Marjı',
      dataIndex: 'margin',
      key: 'margin',
      align: 'right',
      render: (value: number) => (
        <Text type={value >= 0 ? 'success' : 'danger'}>{formatMargin(value)}</Text>
      )
    }
  ];

  const totals = data?.totals;

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Title level={3}>Kârlılık Raporu</Title>

      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Space wrap>
            <RangePicker
              value={dateRange}
              onChange={handleRangeChange}
              allowClear={false}
              format="DD.MM.YYYY"
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={() => loadData(dateRange)}
              loading={loading}
            >
              Yenile
            </Button>
          </Space>

          <Row gutter={16}>
            <Col xs={24} sm={12} md={6}>
              <Card bordered>
                <Statistic
                  title="Toplam Satış"
                  value={totals?.salesAmount ?? 0}
                  precision={2}
                  prefix={baseCurrencySymbol}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card bordered>
                <Statistic
                  title="Toplam Maliyet"
                  value={totals?.costAmount ?? 0}
                  precision={2}
                  prefix={baseCurrencySymbol}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card bordered>
                <Statistic
                  title="Toplam Kâr"
                  value={totals?.profit ?? 0}
                  precision={2}
                  prefix={baseCurrencySymbol}
                  valueStyle={{ color: (totals?.profit ?? 0) >= 0 ? '#3f8600' : '#cf1322' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card bordered>
                <Statistic
                  title="Ortalama Kâr Marjı"
                  value={totals?.margin ?? 0}
                  precision={2}
                  suffix="%"
                  valueStyle={{ color: (totals?.margin ?? 0) >= 0 ? '#3f8600' : '#cf1322' }}
                />
              </Card>
            </Col>
          </Row>
        </Space>
      </Card>

      <Card>
        <Spin spinning={loading}>
          {data?.rows?.length ? (
            <Table<ProfitabilityRow>
              dataSource={data.rows}
              columns={columns}
              rowKey={(record) => `${record.productId}-${record.productName || ''}`}
              pagination={false}
              scroll={{ x: true }}
              summary={() => (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}>
                    <Text strong>Toplam</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right">
                    <Text strong>{formatCurrency(totals?.salesAmount ?? 0)}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} align="right">
                    <Text strong>{formatCurrency(totals?.costAmount ?? 0)}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3} align="right">
                    <Text strong type={(totals?.profit ?? 0) >= 0 ? 'success' : 'danger'}>
                      {formatCurrency(totals?.profit ?? 0)}
                    </Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4} align="right">
                    <Text strong type={(totals?.margin ?? 0) >= 0 ? 'success' : 'danger'}>
                      {formatMargin(totals?.margin ?? 0)}
                    </Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              )}
            />
          ) : (
            <Empty description="Seçilen tarih aralığı için veri bulunamadı" />
          )}
        </Spin>
      </Card>
    </Space>
  );
};

export default ProfitabilityReport;

